import type { TrendingItem } from '@/types';
import { useAppDispatch } from '@/app/hooks';
import { showToast, setTranscriptSearch } from '@/features/ui/uiSlice';
import { fetchStorageAsset } from '@/features/search/searchSlice';
import { setPlaying, updateCurrentTime, requestAutoplay } from '@/features/player/playerSlice';
import { useHoverVideoPreview } from '@/hooks/useHoverVideoPreview';
import demoVideo from '@/assets/videos/Image_from_iOS.MOV';
import styles from './TrendingTile.module.css';

interface TrendingTileProps {
  item: TrendingItem;
  variant?: 'grid';
}

export default function TrendingTile({ item, variant }: TrendingTileProps) {
  const dispatch = useAppDispatch();

  // Use real storage_url when available; hook handles lazy loading + 5 s loop
  const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverVideoPreview(
    item.storage_url
  );

  const handleSendToStoryHub = () => {
    dispatch(
      showToast({
        title: 'Sent to Story Hub',
        message: `Creative brief created for <b style="color:var(--color-text)">${item.title}</b><br/>Format: ${item.platformLabel} vertical · Hook pattern pre-populated`,
      })
    );
  };

  // Clicking the tile thumbnail plays the video in the hero player when storage_url exists
  const handleTileClick = () => {
    if (!item.storage_url) return;
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
    dispatch(requestAutoplay());
    dispatch(setTranscriptSearch(''));
    dispatch(fetchStorageAsset(item.id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={styles.tile} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        className={`${styles.thumb} tile-bg-${item.gradient}`}
        onClick={handleTileClick}
        role={item.storage_url ? 'button' : undefined}
        tabIndex={item.storage_url ? 0 : undefined}
        aria-label={item.storage_url ? `Play ${item.title}` : undefined}
        onKeyDown={item.storage_url ? (e) => e.key === 'Enter' && handleTileClick() : undefined}
        style={item.storage_url ? { cursor: 'pointer' } : undefined}
      >
        <video
          ref={videoRef}
          className={styles.thumbVideo}
          src={item.storage_url ? undefined : demoVideo}
          muted
          loop={!item.storage_url}
          playsInline
          preload={item.storage_url ? 'none' : 'metadata'}
        />
        <span className={styles.matchBadge}>⚡ MATCH</span>
        <span className={styles.platBadge}>
          <span className={`${styles.platIcon} plat-${item.platform}`} />
          {item.platformLabel}
        </span>
        <span className={styles.duration}>{item.duration}</span>
        <span className={styles.clipRange}>{item.clipRange}</span>
        {variant === 'grid' && (
          <div className={styles.thumbTitleOverlay}>
            <span className={styles.thumbTitleText}>{item.title}</span>
          </div>
        )}
      </div>

      {variant !== 'grid' && <div className={styles.body}>
        <p className={styles.hook}>{item.title}</p>
        <div className={styles.matchRow}>
          <span className={styles.label}>Matches</span>
          {item.matchPattern}
          <b className={styles.delta}>{item.trendDelta}</b>
        </div>
        <div className={styles.reachRow}>
          <span className={styles.label}>Projected reach</span>
          <b className={styles.reach}>{item.projectedReach}</b> on {item.platformLabel}
        </div>
        <div className={styles.actions}>
          <button className={styles.btnPrimary} onClick={handleSendToStoryHub}>
            → Send to Story Hub
          </button>
          <button className={styles.btnGhost}>Preview clip</button>
        </div>
      </div>}
    </div>
  );
}
